import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';
import { StaffRole } from '../const';

/**
 * A staff member as the Team screen needs them.
 *
 * The endpoint returns the full user document, which also carries `profile`,
 * `kyc`, `kycStatus`, `referenceToken` and `referralFee`. For an operator
 * account those are empty or defaulted and carry no meaning, so they are
 * deliberately left off this interface — that turns "do not render them" from
 * something a reviewer has to catch into a compile error.
 */
export interface StaffUser {
  id: string;
  email: string;
  username: string;
  /**
   * Typed as a plain string, not StaffRole: a row can come back as
   * `super-admin`, which is not assignable from the UI, and the backend may
   * grow roles this build does not know about.
   */
  role: string;
  status: 'active' | 'inactive';
  isBlocked: boolean;
  /**
   * Present ONLY while the account has no password. It becomes absent — not
   * false — once one is set, so this must always be read for truthiness.
   * It is the only signal for "this operator never finished setting up".
   */
  canCreatePassword?: boolean;
  TFAEnabled: boolean;
  created: number;
  updated: number;
  lang: string;
}

export interface StaffSearchFilters {
  role: string;
  status: 'any' | 'active' | 'inactive';
  /** Strings, not booleans — the API expects "true" / "false". */
  isBlocked: 'any' | 'true' | 'false';
  search: string;
}

export interface StaffSearchResponse {
  users: StaffUser[];
  total: number;
}

export interface CreateStaffPayload {
  email: string;
  role: StaffRole;
  username?: string;
  lang?: 'en' | 'ru' | 'hy';
}

export interface StaffRoleChangeResult {
  id: string;
  previousRole: string;
  role: string;
}

export interface StaffStatusChangeResult {
  id: string;
  status: 'active' | 'inactive';
}

@Injectable({ providedIn: 'root' })
export class StaffService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  /**
   * Creates an operator account. It comes back active but WITHOUT a password,
   * and nothing is emailed — the caller has to tell the person to set one
   * through the ordinary Forgot password flow.
   */
  createStaff(payload: CreateStaffPayload): Observable<StaffUser> {
    return this.networkHelper.post(`${this.hostUrl}/admin/staff`, payload);
  }

  /**
   * Built by whitelisting keys in rather than the `searchUser` style of
   * mutating the caller's object and deleting sentinels. The staff validator
   * is a strict allow-list — one forgotten `delete` when a filter is added
   * later would make every request fail with "Unexpected parameter".
   */
  searchStaff(
    filters: StaffSearchFilters,
    page: number,
    itemsPerPage: number,
    sortBy = '-created'
  ): Observable<StaffSearchResponse> {
    const body: Record<string, unknown> = {
      page,
      itemsPerPage,
      sortBy: StaffService.normalizeSort(sortBy),
    };

    if (filters.role && filters.role !== 'any') {
      body['role'] = filters.role;
    }
    if (filters.status && filters.status !== 'any') {
      body['status'] = filters.status;
    }
    if (filters.isBlocked === 'true' || filters.isBlocked === 'false') {
      body['isBlocked'] = filters.isBlocked;
    }
    const search = (filters.search ?? '').trim();
    if (search) {
      body['search'] = search;
    }

    return this.networkHelper.post(`${this.hostUrl}/admin/staff/search`, body);
  }

  /** Closes every session the operator has, so the new role takes effect. */
  setStaffRole(
    userId: string,
    role: StaffRole
  ): Observable<StaffRoleChangeResult> {
    return this.networkHelper.patch(
      `${this.hostUrl}/admin/staff/${userId}/role`,
      { role }
    );
  }

  /** Closes their sessions and, while inactive, blocks their password reset. */
  setStaffStatus(
    userId: string,
    status: 'active' | 'inactive'
  ): Observable<StaffStatusChangeResult> {
    return this.networkHelper.patch(
      `${this.hostUrl}/admin/staff/${userId}/status`,
      { status }
    );
  }

  /** The API rejects a sortBy with no leading sign as "Invalid sort direction". */
  private static normalizeSort(sortBy: string): string {
    if (!sortBy) return '-created';
    return sortBy.startsWith('-') || sortBy.startsWith('+')
      ? sortBy
      : `-${sortBy}`;
  }
}
